import React, { useState, useEffect, useMemo } from 'react';
import { pdf } from '@react-pdf/renderer';
import { FileSignature, User, Building2, Percent, StickyNote, Download, FolderOpen, Save, Trash2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { formatCurrency, formatPercentage } from '../utils/formatters';
import { IOIPDF } from './PDFDocument';
import { calculateSizer } from './LoanSizer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Dropdown options ──────────────────────────────────────────────────────────

const PRODUCTS = ['Bridge', 'Construction'];
const LOAN_PURPOSES = ['Purchase', 'Refinance', 'Cash-Out Refinance', 'Rate & Term Refinance', 'Ground-Up Construction'];
const PROPERTY_TYPES = ['Single Family', 'Condominium', 'Townhome', 'Multifamily', 'Mixed Use', 'Residential Land', 'Commercial Land'];
const RATE_TYPES = ['Fixed', 'Floating'];
const INTEREST_RATES = ['9.99%', '10.50%', '10.99%', 'Custom'];
const INTEREST_GUARANTY = ['None', '3 Months', '6 Months', '9 Months', '12 Months', 'Custom'];
const INTEREST_RESERVE = ['None', '3 Months', '6 Months', '9 Months', '12 Months', 'Custom'];
const EXTENSION_OPTIONS = ['None', 'One 6-Month Extension at 1% of Loan Amount', 'One 12-Month Extension at 1% of Loan Amount', 'Custom'];
const LOAN_TERMS = ['12 Months', '18 Months', '24 Months', 'Custom'];
const BROKER_COMP = ['0%', '1%', '1.5%', '2%', '2.5%', '3%', 'Custom'];
const RATE_LOCK = ['None', '30 Days', '60 Days', 'Through End of Year', 'Custom'];
const LTV_OPTIONS = ['50%', '55%', '60%', '65%', 'Custom'];

export const PREDEFINED_NOTES = [
  'Subject to underwriting approval.',
  'Subject to satisfactory appraisal.',
  'Subject to title review.',
  'Subject to verification of liquidity.',
  'Subject to committee approval.',
  'Subject to acceptable insurance.',
  'Subject to environmental review.',
  'Subject to borrower financial review.',
  'Loan proceeds subject to final closing statement.',
  'Additional conditions may apply.',
  'Borrower to provide updated organizational documents.',
  'Borrower to provide current bank statements.',
  'Personal guarantee required.',
  'Corporate guarantee required.',
  'Final terms subject to executed Term Sheet.',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const FieldLabel = ({ children }) => (
  <Label className="text-xs text-[#637888] uppercase tracking-wide">{children}</Label>
);

const CurrencyField = ({ label, value, onChange, testid, placeholder }) => {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState('');
  const display = focused
    ? raw
    : (value === '' || value === 0 || value === undefined ? '' : Number(value).toLocaleString('en-US'));
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="relative mt-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">$</span>
        <Input
          type="text"
          inputMode="decimal"
          value={display}
          placeholder={placeholder || '0'}
          onFocus={() => { setFocused(true); setRaw(value === '' || value === 0 ? '' : String(value)); }}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            const r = e.target.value.replace(/[^0-9.]/g, '');
            setRaw(r);
            onChange(r === '' ? '' : Math.max(0, parseFloat(r) || 0));
          }}
          data-testid={testid}
          className="pl-7 font-mono-number"
        />
      </div>
    </div>
  );
};

const TextField = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1"
    />
  </div>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="mt-1">
        <SelectValue placeholder="Select..." />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// Dropdown with a Custom option that reveals a free input
const SelectWithCustom = ({ label, choice, custom, onChoice, onCustom, options, testid, customPlaceholder, suffix }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <Select value={choice} onValueChange={onChoice}>
      <SelectTrigger data-testid={testid} className="mt-1">
        <SelectValue placeholder="Select..." />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    {choice === 'Custom' && (
      <div className="relative mt-2">
        <Input
          type="text"
          value={custom}
          placeholder={customPlaceholder || 'Enter custom value'}
          onChange={(e) => onCustom(suffix ? e.target.value.replace(/[^0-9.]/g, '') : e.target.value)}
          data-testid={`${testid}-custom`}
          className={`font-mono-number ${suffix ? (suffix === '%' ? 'pr-7' : 'pr-16') : ''}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#637888] font-mono">{suffix}</span>
        )}
      </div>
    )}
  </div>
);

const resolve = (choice, custom) => (choice === 'Custom' ? custom : choice);

// Resolve a Custom selection appending the correct unit when a bare number is entered
const withUnit = (choice, custom, unit) => {
  if (choice !== 'Custom') return choice;
  const v = (custom || '').trim();
  if (!v) return '';
  if (/^[0-9.]+$/.test(v)) return unit === '%' ? `${v}%` : `${v} ${unit}`;
  return v;
};

const monthsToChoice = (m, options) => {
  const n = parseFloat(m);
  if (!n) return 'None';
  const label = `${Math.round(n)} Months`;
  return options.includes(label) ? label : 'Custom';
};

const termToChoice = (m) => {
  const n = parseInt(m) || 12;
  const label = `${n} Months`;
  return LOAN_TERMS.includes(label) ? { choice: label, custom: '' } : { choice: 'Custom', custom: String(n) };
};

const ltvToChoice = (ltv) => {
  const n = parseFloat(ltv);
  if (!n) return { ltvChoice: '55%', ltvCustom: '' };
  const preset = [50, 55, 60, 65].find((p) => Math.abs(p - n) < 0.25);
  return preset
    ? { ltvChoice: `${preset}%`, ltvCustom: '' }
    : { ltvChoice: 'Custom', ltvCustom: n.toFixed(2) };
};

// ── Initial form state ────────────────────────────────────────────────────────

const INITIAL_FORM = {
  borrowerName: '',
  borrowingEntity: '',
  propertyAddress: '',
  propertyType: 'Single Family',
  product: 'Bridge',
  loanPurpose: 'Purchase',
  purchasePrice: '',
  appraisedValue: '',
  ltvChoice: '55%',
  ltvCustom: '',
  ltc: '',
  interestRateChoice: '9.99%',
  interestRateCustom: '',
  rateType: 'Fixed',
  loanTermChoice: '12 Months',
  loanTermCustom: '',
  interestGuarantyChoice: 'None',
  interestGuarantyCustom: '',
  interestReserveChoice: 'None',
  interestReserveCustom: '',
  lenderPoints: '2',
  brokerCompChoice: '2%',
  brokerCompCustom: '',
  extensionChoice: 'None',
  extensionCustom: '',
  rateLockChoice: 'None',
  rateLockCustom: '',
};

// ── Main Component ────────────────────────────────────────────────────────────

export default function IOIGenerator({ loanOfficer, controlsRef }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState('');
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [customNotes, setCustomNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [showLoad, setShowLoad] = useState(false);
  const [scenarioName, setScenarioName] = useState('');
  const [savedIOIs, setSavedIOIs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingIOIs, setLoadingIOIs] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const set = (key) => (val) => setForm((prev) => ({ ...prev, [key]: val }));

  // Fetch saved Bridge + Construction scenarios
  useEffect(() => {
    const load = async () => {
      if (!loanOfficer?.id) return;
      const merged = [];
      try {
        const res = await fetch(`${API_URL}/api/scenarios/${loanOfficer.id}`);
        if (res.ok) {
          const list = await res.json();
          list
            .filter((s) => s.loan_type === 'bridge_purchase' || s.loan_type === 'bridge_refinance')
            .forEach((s) => merged.push({
              id: `bridge:${s.id}`,
              label: `Bridge — ${s.project_name || s.property_address} (${new Date(s.created_at).toLocaleDateString()})`,
            }));
        }
      } catch { /* ignore */ }
      try {
        const res = await fetch(`${API_URL}/api/sizer-scenarios?officer_id=${loanOfficer.id}`);
        if (res.ok) {
          const list = await res.json();
          list.forEach((s) => merged.push({
            id: `construction:${s.id}`,
            label: `Construction — ${s.name} (${new Date(s.created_at).toLocaleDateString()})`,
            doc: s,
          }));
        }
      } catch { /* ignore */ }
      setScenarios(merged);
    };
    load();
  }, [loanOfficer?.id]);

  const populateFromBridge = (sc) => {
    const inp = sc.inputs || {};
    const m = sc.metrics || {};
    const isPurchase = sc.loan_type === 'bridge_purchase';
    const purchasePrice = parseFloat(m.purchasePrice || inp.purchasePrice) || '';
    const appraised = parseFloat(m.propertyValue || m.afterRepairValue || inp.propertyValue) || purchasePrice || '';
    const loanAmount = parseFloat(m.loanAmount) || '';
    const scenarioLTV = appraised && loanAmount ? (loanAmount / appraised) * 100 : 0;
    setForm((prev) => ({
      ...prev,
      product: 'Bridge',
      loanPurpose: isPurchase ? 'Purchase' : 'Cash-Out Refinance',
      propertyAddress: sc.property_address || '',
      purchasePrice,
      appraisedValue: appraised || purchasePrice,
      ...ltvToChoice(scenarioLTV),
      ltc: '',
      interestRateChoice: 'Custom',
      interestRateCustom: inp.interestRate ? String(inp.interestRate) : '',
      rateType: inp.rateType === 'Variable' ? 'Floating' : (inp.rateType || 'Fixed'),
      ...(() => { const t = termToChoice(inp.loanTerm || 12); return { loanTermChoice: t.choice, loanTermCustom: t.custom }; })(),
      interestReserveChoice: monthsToChoice(inp.interestReserveMonths, INTEREST_RESERVE),
      interestReserveCustom: inp.interestReserveMonths ? String(inp.interestReserveMonths) : '',
      lenderPoints: '2',
      brokerCompChoice: '2%',
    }));
  };

  const populateFromConstruction = (doc) => {
    const calc = calculateSizer({
      landCost: doc.land_cost, hardCosts: doc.hard_costs, softCosts: doc.soft_costs,
      arv: doc.arv, annualRate: doc.annual_rate, termMonths: doc.term_months,
      closingCostRate: doc.closing_cost_rate, irCoverage: doc.ir_coverage,
      maxLTV: doc.max_ltv, maxLTC: doc.max_ltc,
      landValue: doc.land_value ?? doc.land_cost, currentDebt: doc.current_debt || 0,
      midpointPct: doc.midpoint_pct ?? ((doc.max_ltv + doc.max_ltc) / 2),
    });
    setForm((prev) => ({
      ...prev,
      product: 'Construction',
      loanPurpose: 'Purchase',
      propertyType: 'Single Family',
      propertyAddress: doc.property_address || '',
      purchasePrice: doc.land_cost || '',
      appraisedValue: doc.arv || '',
      ...ltvToChoice(calc.actualLTV),
      ltc: calc.actualLTC ? calc.actualLTC.toFixed(2) : '',
      interestRateChoice: 'Custom',
      interestRateCustom: doc.annual_rate ? String(doc.annual_rate) : '',
      rateType: 'Fixed',
      ...(() => { const t = termToChoice(doc.term_months || 12); return { loanTermChoice: t.choice, loanTermCustom: t.custom }; })(),
      interestReserveChoice: monthsToChoice(calc.irMonths, INTEREST_RESERVE),
      interestReserveCustom: calc.irMonths ? calc.irMonths.toFixed(1) : '',
    }));
  };

  const handleScenarioSelect = async (id) => {
    setSelectedScenarioId(id);
    const [kind, scenarioId] = id.split(':');
    if (kind === 'construction') {
      const found = scenarios.find((s) => s.id === id);
      if (found?.doc) populateFromConstruction(found.doc);
    } else {
      try {
        const res = await fetch(`${API_URL}/api/scenarios/${loanOfficer.id}/${scenarioId}`);
        if (res.ok) populateFromBridge(await res.json());
      } catch { /* ignore */ }
    }
  };

  const toggleNote = (note) => {
    setSelectedNotes((prev) =>
      prev.includes(note) ? prev.filter((n) => n !== note) : [...prev, note]
    );
  };

  // Resolved values for the PDF
  const resolvedLTV = form.ltvChoice === 'Custom' ? parseFloat(form.ltvCustom) : parseFloat(form.ltvChoice);

  const computedLoanAmount = useMemo(() => {
    const val = parseFloat(form.appraisedValue);
    return val > 0 && resolvedLTV > 0 ? Math.round(val * (resolvedLTV / 100)) : '';
  }, [form.appraisedValue, resolvedLTV]);

  const pdfData = useMemo(() => ({
    borrowerName: form.borrowerName,
    borrowingEntity: form.borrowingEntity,
    propertyAddress: form.propertyAddress,
    propertyType: form.propertyType,
    product: form.product,
    loanPurpose: form.loanPurpose,
    purchasePrice: form.appraisedValue,
    appraisedValue: form.appraisedValue,
    loanAmount: computedLoanAmount,
    ltv: resolvedLTV > 0 ? resolvedLTV.toFixed(2) : '',
    ltc: form.ltc,
    interestRate: form.interestRateChoice === 'Custom'
      ? (form.interestRateCustom ? `${form.interestRateCustom.replace('%', '')}%` : '')
      : form.interestRateChoice,
    rateType: form.rateType,
    loanTerm: withUnit(form.loanTermChoice, form.loanTermCustom, 'Months'),
    interestGuaranty: withUnit(form.interestGuarantyChoice, form.interestGuarantyCustom, 'Months'),
    interestReserve: withUnit(form.interestReserveChoice, form.interestReserveCustom, 'Months'),
    lenderPoints: form.lenderPoints,
    brokerPoints: withUnit(form.brokerCompChoice, form.brokerCompCustom, '%'),
    extensionOption: resolve(form.extensionChoice, form.extensionCustom),
    rateLock: withUnit(form.rateLockChoice, form.rateLockCustom, 'Days'),
  }), [form]);

  const notesList = useMemo(() => {
    const custom = customNotes.split('\n').map((n) => n.trim()).filter(Boolean);
    return [...selectedNotes, ...custom];
  }, [selectedNotes, customNotes]);

  const canGenerate = form.propertyAddress && computedLoanAmount;

  // Generate on click — awaits full render (incl. signature font) so the
  // loan officer's cursive signature is always embedded in the PDF.
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const blob = await pdf(<IOIPDF data={pdfData} notes={notesList} loanOfficer={loanOfficer} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `indication-of-interest-${(form.propertyAddress || 'property').split(',')[0].replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Failed to generate PDF', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveIOI = async () => {
    if (!scenarioName.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/ioi-scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: scenarioName.trim(),
          officer_id: loanOfficer?.id || 'unknown',
          form,
          selected_notes: selectedNotes,
          custom_notes: customNotes,
        }),
      });
      if (!res.ok) throw new Error();
      showToast('IOI scenario saved successfully');
      setShowSave(false);
      setScenarioName('');
    } catch {
      showToast('Failed to save scenario', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadIOIOpen = async () => {
    setLoadingIOIs(true);
    setShowLoad(true);
    try {
      const res = await fetch(`${API_URL}/api/ioi-scenarios${loanOfficer?.id ? `?officer_id=${loanOfficer.id}` : ''}`);
      if (!res.ok) throw new Error();
      setSavedIOIs(await res.json());
    } catch {
      showToast('Failed to load scenarios', 'error');
    } finally {
      setLoadingIOIs(false);
    }
  };

  const handleLoadIOI = (s) => {
    setForm({ ...INITIAL_FORM, ...(s.form || {}) });
    setSelectedNotes(s.selected_notes || []);
    setCustomNotes(s.custom_notes || '');
    setShowLoad(false);
    showToast(`Loaded "${s.name}"`);
  };

  const handleDeleteIOI = async (id, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_URL}/api/ioi-scenarios/${id}`, { method: 'DELETE' });
      setSavedIOIs((prev) => prev.filter((s) => s.id !== id));
      showToast('Scenario deleted');
    } catch {
      showToast('Failed to delete scenario', 'error');
    }
  };

  React.useEffect(() => {
    if (controlsRef) controlsRef.current = { openSave: () => setShowSave(true), openLoad: handleLoadIOIOpen };
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

      {/* LEFT — Inputs */}
      <div className="lg:col-span-3 space-y-6">

        {/* Load from saved scenario */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <FolderOpen className="w-4 h-4" strokeWidth={1.5} />
              Load From Saved Scenario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FieldLabel>Select a saved Bridge or Construction scenario (optional)</FieldLabel>
            <Select value={selectedScenarioId} onValueChange={handleScenarioSelect}>
              <SelectTrigger data-testid="ioi-scenario-select" className="mt-1">
                <SelectValue placeholder={scenarios.length ? 'Select a saved scenario...' : 'No saved scenarios found'} />
              </SelectTrigger>
              <SelectContent>
                {scenarios.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#637888] mt-2">
              All fields below auto-populate from the selected scenario and remain fully editable.
            </p>
          </CardContent>
        </Card>

        {/* Borrower & Property */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <User className="w-4 h-4" strokeWidth={1.5} />
              Borrower &amp; Property Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField label="Sponsor Name" value={form.borrowerName} onChange={set('borrowerName')} testid="ioi-sponsor-name" placeholder="e.g., John Smith" />
              <TextField label="Borrowing Entity" value={form.borrowingEntity} onChange={set('borrowingEntity')} testid="ioi-borrowing-entity" placeholder="e.g., Smith Holdings LLC" />
              <div className="md:col-span-2">
                <TextField label="Property Address" value={form.propertyAddress} onChange={set('propertyAddress')} testid="ioi-property-address" placeholder="e.g., 123 Ocean Drive, Miami Beach FL 33139" />
              </div>
              <SelectField label="Property Type" value={form.propertyType} onChange={set('propertyType')} options={PROPERTY_TYPES} testid="ioi-property-type" />
              <SelectField label="Product" value={form.product} onChange={set('product')} options={PRODUCTS} testid="ioi-product" />
              <SelectField label="Loan Purpose" value={form.loanPurpose} onChange={set('loanPurpose')} options={LOAN_PURPOSES} testid="ioi-loan-purpose" />
            </div>
          </CardContent>
        </Card>

        {/* Loan Terms */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <Percent className="w-4 h-4" strokeWidth={1.5} />
              Loan Terms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CurrencyField
                label={form.loanPurpose === 'Purchase' ? 'Purchase Price' : 'Estimated Value'}
                value={form.appraisedValue}
                onChange={set('appraisedValue')}
                testid="ioi-estimated-value"
              />
              <SelectWithCustom
                label="LTV"
                choice={form.ltvChoice}
                custom={form.ltvCustom}
                onChoice={set('ltvChoice')}
                onCustom={set('ltvCustom')}
                options={LTV_OPTIONS}
                testid="ioi-ltv"
                customPlaceholder="e.g., 62.5"
                suffix="%"
              />
              <div>
                <FieldLabel>Loan Amount (auto)</FieldLabel>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">$</span>
                  <Input
                    type="text"
                    readOnly
                    value={computedLoanAmount ? Number(computedLoanAmount).toLocaleString('en-US') : ''}
                    data-testid="ioi-loan-amount"
                    className="pl-7 font-mono-number bg-slate-50 text-slate-700 cursor-default"
                    tabIndex={-1}
                  />
                </div>
                <p className="text-xs text-[#637888] mt-1">
                  {form.loanPurpose === 'Purchase' ? 'Purchase Price' : 'Estimated Value'} × LTV
                </p>
              </div>
              {form.product.includes('Construction') && (
                <div>
                  <FieldLabel>LTC</FieldLabel>
                  <div className="relative mt-1">
                    <Input type="number" step="0.01" value={form.ltc} onChange={(e) => set('ltc')(e.target.value)} data-testid="ioi-ltc" className="pr-7 font-mono-number" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">%</span>
                  </div>
                </div>
              )}
              <SelectWithCustom
                label="Interest Rate"
                choice={form.interestRateChoice}
                custom={form.interestRateCustom}
                onChoice={set('interestRateChoice')}
                onCustom={set('interestRateCustom')}
                options={INTEREST_RATES}
                testid="ioi-interest-rate"
                customPlaceholder="e.g., 11.25"
                suffix="%"
              />
              <SelectField label="Rate Type" value={form.rateType} onChange={set('rateType')} options={RATE_TYPES} testid="ioi-rate-type" />
              <SelectWithCustom
                label="Loan Term"
                choice={form.loanTermChoice}
                custom={form.loanTermCustom}
                onChoice={set('loanTermChoice')}
                onCustom={set('loanTermCustom')}
                options={LOAN_TERMS}
                testid="ioi-loan-term"
                customPlaceholder="e.g., 36"
                suffix="Months"
              />
              <SelectWithCustom
                label="Interest Guaranty"
                choice={form.interestGuarantyChoice}
                custom={form.interestGuarantyCustom}
                onChoice={set('interestGuarantyChoice')}
                onCustom={set('interestGuarantyCustom')}
                options={INTEREST_GUARANTY}
                testid="ioi-interest-guaranty"
                customPlaceholder="e.g., 4"
                suffix="Months"
              />
              <SelectWithCustom
                label="Interest Reserve"
                choice={form.interestReserveChoice}
                custom={form.interestReserveCustom}
                onChoice={set('interestReserveChoice')}
                onCustom={set('interestReserveCustom')}
                options={INTEREST_RESERVE}
                testid="ioi-interest-reserve"
                customPlaceholder="e.g., 7.2"
                suffix="Months"
              />
              <div>
                <FieldLabel>Lender Points</FieldLabel>
                <div className="relative mt-1">
                  <Input type="number" step="0.25" value={form.lenderPoints} onChange={(e) => set('lenderPoints')(e.target.value)} data-testid="ioi-lender-points" className="pr-7 font-mono-number" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">%</span>
                </div>
              </div>
              <SelectWithCustom
                label="Broker Compensation"
                choice={form.brokerCompChoice}
                custom={form.brokerCompCustom}
                onChoice={set('brokerCompChoice')}
                onCustom={set('brokerCompCustom')}
                options={BROKER_COMP}
                testid="ioi-broker-comp"
                customPlaceholder="e.g., 1.75"
                suffix="%"
              />
              <SelectWithCustom
                label="Rate Lock"
                choice={form.rateLockChoice}
                custom={form.rateLockCustom}
                onChoice={set('rateLockChoice')}
                onCustom={set('rateLockCustom')}
                options={RATE_LOCK}
                testid="ioi-rate-lock"
                customPlaceholder="e.g., 45"
                suffix="Days"
              />
              <div className="md:col-span-2">
                <SelectWithCustom
                  label="Extension Option"
                  choice={form.extensionChoice}
                  custom={form.extensionCustom}
                  onChoice={set('extensionChoice')}
                  onCustom={set('extensionCustom')}
                  options={EXTENSION_OPTIONS}
                  testid="ioi-extension-option"
                  customPlaceholder="e.g., One 6-Month Extension at 1% in / 1% out lender fee"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <StickyNote className="w-4 h-4" strokeWidth={1.5} />
              Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <FieldLabel>Predefined Notes (select any that apply)</FieldLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {PREDEFINED_NOTES.map((note, i) => (
                  <label key={note} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                    <Checkbox
                      checked={selectedNotes.includes(note)}
                      onCheckedChange={() => toggleNote(note)}
                      data-testid={`ioi-note-${i}`}
                      className="mt-0.5"
                    />
                    <span>{note}</span>
                  </label>
                ))}
              </div>
            </div>
            <Separator />
            <div>
              <FieldLabel>Custom Notes (one per line)</FieldLabel>
              <Textarea
                value={customNotes}
                onChange={(e) => setCustomNotes(e.target.value)}
                placeholder="Enter any additional custom notes, one per line..."
                data-testid="ioi-custom-notes"
                className="mt-1 min-h-[80px]"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* RIGHT — Summary & Generate */}
      <div className="lg:col-span-2">
        <div className="sticky top-6">
          <Card className="border border-slate-200 shadow-lg overflow-hidden" data-testid="ioi-summary-card">
            <div className="h-1 bg-[#FFFF00]" />
            <div className="bg-[#002F87] text-white px-6 py-4">
              <h2 className="font-heading font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                <FileSignature className="w-4 h-4" strokeWidth={1.5} />
                Indication of Interest
              </h2>
              <p className="text-xs text-white/70 mt-1">{form.product} — {form.loanPurpose}</p>
            </div>
            <CardContent className="p-6 space-y-6">
              <div className="p-4 bg-[#002F87] rounded-md">
                <p className="text-xs text-white/80 uppercase tracking-wide mb-1">Loan Amount</p>
                <p className="font-mono-number text-2xl font-bold text-white" data-testid="ioi-summary-loan-amount">
                  {computedLoanAmount ? formatCurrency(computedLoanAmount) : '—'}
                </p>
              </div>

              <div className="space-y-2 text-sm">
                {[
                  ['Sponsor', form.borrowerName || '—'],
                  ['Property', form.propertyAddress || '—'],
                  ['Property Type', form.propertyType],
                  [form.loanPurpose === 'Purchase' ? 'Purchase Price' : 'Estimated Value', form.appraisedValue ? formatCurrency(form.appraisedValue) : '—'],
                  ['LTV', resolvedLTV > 0 ? formatPercentage(resolvedLTV, 2) : '—'],
                  ...(form.product.includes('Construction') && form.ltc ? [['LTC', formatPercentage(parseFloat(form.ltc), 2)]] : []),
                  ['Rate', pdfData.interestRate || '—'],
                  ['Term', pdfData.loanTerm || '—'],
                  ['Rate Type', form.rateType],
                  ['Extension', pdfData.extensionOption || 'None'],
                  ['Notes Selected', String(notesList.length)],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-3">
                    <span className="text-slate-600 flex-shrink-0">{label}</span>
                    <span className="font-mono-number text-slate-900 text-right truncate">{value}</span>
                  </div>
                ))}
              </div>

              <Separator />

              <div className="text-xs text-[#637888] space-y-1">
                <div className="flex justify-between">
                  <span>Loan Officer</span>
                  <span className="text-slate-800 font-medium">{loanOfficer?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span>Date Generated</span>
                  <span className="text-slate-800 font-medium">{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>
                {loanOfficer?.applicationLink && (
                  <div className="flex justify-between items-center gap-2">
                    <span className="flex-shrink-0">Application Link</span>
                    <a
                      href={`https://${loanOfficer.applicationLink.replace(/^https?:\/\//, '')}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="ioi-application-link"
                      className="text-[#002F87] font-medium underline truncate hover:text-[#00256B]"
                    >
                      {loanOfficer.applicationLink}
                    </a>
                  </div>
                )}
              </div>

              <Button
                data-testid="ioi-generate-btn"
                onClick={handleGenerate}
                className="w-full h-12 bg-[#FFFF00] hover:bg-[#E6E600] text-black font-heading font-semibold uppercase tracking-wide"
                disabled={generating || !canGenerate}
              >
                {generating ? (
                  <>Generating PDF...</>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" strokeWidth={2} />
                    Generate Indication of Interest
                  </>
                )}
              </Button>

              {/* Scenario Tools */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowSave(true)}
                  data-testid="ioi-save-btn"
                  className="text-xs text-[#637888] hover:text-[#002F87]"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  Save
                </Button>
                <Button
                  variant="outline"
                  onClick={handleLoadIOIOpen}
                  data-testid="ioi-load-btn"
                  className="text-xs text-[#637888] hover:text-[#002F87]"
                >
                  <FolderOpen className="w-3.5 h-3.5 mr-1.5" />
                  Load
                </Button>
              </div>
              {!canGenerate && (
                <p className="text-xs text-[#637888] text-center">
                  Enter at least a Property Address and {form.loanPurpose === 'Purchase' ? 'Purchase Price' : 'Estimated Value'} to generate.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
